import type { StateCode } from "./platform";

/**
 * School boards a school can be affiliated to: national boards, every state's secondary and
 * higher-secondary boards, open schools and the international boards Indian schools use. From
 * Wikipedia's "List of Indian education boards" (November 2026). A board that isn't listed is
 * "other", with its name typed in.
 */
export type BoardKind = "national" | "international" | "state" | "open" | "religious";

export interface SchoolBoard {
  key: string;
  name: string;
  /** Abbreviation people use, e.g. "CBSE". */
  short: string;
  kind: BoardKind;
  /** GST state code for a state board; null for national and international boards. */
  stateCode: StateCode | null;
}

export const SCHOOL_BOARDS = [
  {
    key: "cbse",
    name: "Central Board of Secondary Education",
    short: "CBSE",
    kind: "national",
    stateCode: null,
  },
  {
    key: "cisce",
    name: "Council for the Indian School Certificate Examinations (ICSE / ISC)",
    short: "CISCE",
    kind: "national",
    stateCode: null,
  },
  {
    key: "nios",
    name: "National Institute of Open Schooling",
    short: "NIOS",
    kind: "open",
    stateCode: null,
  },
  {
    key: "ib",
    name: "International Baccalaureate",
    short: "IB",
    kind: "international",
    stateCode: null,
  },
  {
    key: "caie",
    name: "Cambridge Assessment International Education (IGCSE)",
    short: "Cambridge",
    kind: "international",
    stateCode: null,
  },
  {
    key: "edexcel",
    name: "Pearson Edexcel",
    short: "Edexcel",
    kind: "international",
    stateCode: null,
  },
  {
    key: "bseap",
    name: "Andhra Pradesh Board of Secondary Education",
    short: "BSEAP",
    kind: "state",
    stateCode: "37",
  },
  {
    key: "bieap",
    name: "Board of Intermediate Education, Andhra Pradesh",
    short: "BIEAP",
    kind: "state",
    stateCode: "37",
  },
  {
    key: "aposs",
    name: "Andhra Pradesh Open School Society",
    short: "APOSS",
    kind: "open",
    stateCode: "37",
  },
  {
    key: "seba",
    name: "Board of Secondary Education, Assam",
    short: "SEBA",
    kind: "state",
    stateCode: "18",
  },
  {
    key: "ahsec",
    name: "Assam Higher Secondary Education Council",
    short: "AHSEC",
    kind: "state",
    stateCode: "18",
  },
  {
    key: "bseb",
    name: "Bihar School Examination Board",
    short: "BSEB",
    kind: "state",
    stateCode: "10",
  },
  {
    key: "bbose",
    name: "Bihar Board of Open Schooling and Examination",
    short: "BBOSE",
    kind: "open",
    stateCode: "10",
  },
  {
    key: "cgbse",
    name: "Chhattisgarh Board of Secondary Education",
    short: "CGBSE",
    kind: "state",
    stateCode: "22",
  },
  {
    key: "cgsos",
    name: "Chhattisgarh State Open School",
    short: "CGSOS",
    kind: "open",
    stateCode: "22",
  },
  {
    key: "gbshse",
    name: "Goa Board of Secondary and Higher Secondary Education",
    short: "GBSHSE",
    kind: "state",
    stateCode: "30",
  },
  {
    key: "gseb",
    name: "Gujarat Secondary and Higher Secondary Education Board",
    short: "GSEB",
    kind: "state",
    stateCode: "24",
  },
  {
    key: "hbse",
    name: "Haryana Board of School Education",
    short: "HBSE",
    kind: "state",
    stateCode: "06",
  },
  {
    key: "hpbose",
    name: "Himachal Pradesh Board of School Education",
    short: "HPBOSE",
    kind: "state",
    stateCode: "02",
  },
  {
    key: "jkbose",
    name: "Jammu and Kashmir State Board of School Education",
    short: "JKBOSE",
    kind: "state",
    stateCode: "01",
  },
  { key: "jac", name: "Jharkhand Academic Council", short: "JAC", kind: "state", stateCode: "20" },
  {
    key: "kseeb",
    name: "Karnataka Secondary Education Examination Board",
    short: "KSEEB",
    kind: "state",
    stateCode: "29",
  },
  {
    key: "dpue",
    name: "Department of Pre-University Education, Karnataka",
    short: "DPUE",
    kind: "state",
    stateCode: "29",
  },
  {
    key: "kbpe",
    name: "Kerala Board of Public Examination",
    short: "KBPE",
    kind: "state",
    stateCode: "32",
  },
  {
    key: "dhse_kerala",
    name: "Directorate of General Education (Higher Secondary Wing), Kerala",
    short: "DHSE",
    kind: "state",
    stateCode: "32",
  },
  {
    key: "vhse_kerala",
    name: "Board of Vocational Higher Secondary Education, Kerala",
    short: "VHSE",
    kind: "state",
    stateCode: "32",
  },
  {
    key: "mpbse",
    name: "Madhya Pradesh Board of Secondary Education",
    short: "MPBSE",
    kind: "state",
    stateCode: "23",
  },
  {
    key: "mpsos",
    name: "Madhya Pradesh State Open School Education Board",
    short: "MPSOS",
    kind: "open",
    stateCode: "23",
  },
  {
    key: "msbshse",
    name: "Maharashtra State Board of Secondary and Higher Secondary Education",
    short: "MSBSHSE",
    kind: "state",
    stateCode: "27",
  },
  {
    key: "bsem",
    name: "Board of Secondary Education, Manipur",
    short: "BSEM",
    kind: "state",
    stateCode: "14",
  },
  {
    key: "cohsem",
    name: "Council of Higher Secondary Education, Manipur",
    short: "COHSEM",
    kind: "state",
    stateCode: "14",
  },
  {
    key: "mbose",
    name: "Meghalaya Board of School Education",
    short: "MBOSE",
    kind: "state",
    stateCode: "17",
  },
  {
    key: "mbse",
    name: "Mizoram Board of School Education",
    short: "MBSE",
    kind: "state",
    stateCode: "15",
  },
  {
    key: "nbse",
    name: "Nagaland Board of School Education",
    short: "NBSE",
    kind: "state",
    stateCode: "13",
  },
  {
    key: "bse_odisha",
    name: "Board of Secondary Education, Odisha",
    short: "BSE Odisha",
    kind: "state",
    stateCode: "21",
  },
  {
    key: "chse_odisha",
    name: "Council of Higher Secondary Education, Odisha",
    short: "CHSE Odisha",
    kind: "state",
    stateCode: "21",
  },
  {
    key: "pseb",
    name: "Punjab School Education Board",
    short: "PSEB",
    kind: "state",
    stateCode: "03",
  },
  {
    key: "rbse",
    name: "Board of Secondary Education, Rajasthan",
    short: "RBSE",
    kind: "state",
    stateCode: "08",
  },
  {
    key: "rsos",
    name: "Rajasthan State Open School",
    short: "RSOS",
    kind: "open",
    stateCode: "08",
  },
  {
    key: "dge_tn",
    name: "Directorate of Government Examinations, Tamil Nadu",
    short: "DGE TN",
    kind: "state",
    stateCode: "33",
  },
  {
    key: "bse_ts",
    name: "Board of Secondary Education, Telangana State",
    short: "BSE TS",
    kind: "state",
    stateCode: "36",
  },
  {
    key: "tsbie",
    name: "Telangana State Board of Intermediate Education",
    short: "TSBIE",
    kind: "state",
    stateCode: "36",
  },
  {
    key: "toss",
    name: "Telangana Open School Society",
    short: "TOSS",
    kind: "open",
    stateCode: "36",
  },
  {
    key: "tbse",
    name: "Tripura Board of Secondary Education",
    short: "TBSE",
    kind: "state",
    stateCode: "16",
  },
  {
    key: "upmsp",
    name: "Uttar Pradesh Madhyamik Shiksha Parishad",
    short: "UPMSP",
    kind: "state",
    stateCode: "09",
  },
  {
    key: "ubse",
    name: "Uttarakhand Board of School Education",
    short: "UBSE",
    kind: "state",
    stateCode: "05",
  },
  {
    key: "wbbse",
    name: "West Bengal Board of Secondary Education",
    short: "WBBSE",
    kind: "state",
    stateCode: "19",
  },
  {
    key: "wbchse",
    name: "West Bengal Council of Higher Secondary Education",
    short: "WBCHSE",
    kind: "state",
    stateCode: "19",
  },
  {
    key: "wbcros",
    name: "West Bengal Council of Rabindra Open Schooling",
    short: "WBCROS",
    kind: "open",
    stateCode: "19",
  },
  {
    key: "wbbme",
    name: "West Bengal Board of Madrasah Education",
    short: "WBBME",
    kind: "religious",
    stateCode: "19",
  },
] as const satisfies readonly SchoolBoard[];

/** Every board key, plus "other" for a board typed in by hand. */
export type BoardKey = (typeof SCHOOL_BOARDS)[number]["key"] | "other";

export const BOARD_KEYS: [BoardKey, ...BoardKey[]] = [
  "other",
  ...SCHOOL_BOARDS.map((board): BoardKey => board.key),
];

const BOARD_BY_KEY = new Map<string, SchoolBoard>(SCHOOL_BOARDS.map((board) => [board.key, board]));

export function findBoard(key: string): SchoolBoard | null {
  return BOARD_BY_KEY.get(key) ?? null;
}

/** How a school's board reads on screen and on contracts: "CBSE", or the typed name for other. */
export function boardLabel(key: string, otherName?: string | null): string {
  if (key === "other") return otherName?.trim() || "Other board";
  const board = findBoard(key);
  if (!board) return key;
  return board.kind === "state" || board.kind === "open" || board.kind === "religious"
    ? `${board.name} (${board.short})`
    : board.short;
}

/** National and international boards first, then the given state's boards, then the rest. */
export function boardsFor(stateCode: StateCode | null | undefined): SchoolBoard[] {
  const rank = (board: SchoolBoard) =>
    board.stateCode === null ? 0 : board.stateCode === stateCode ? 1 : 2;
  return [...SCHOOL_BOARDS].sort((a, b) => rank(a) - rank(b));
}
